/* eslint-disable react-hooks/exhaustive-deps */
// src/pages/profile/Profile.js
import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import { useForm, Controller } from "react-hook-form";
import FloatingInput from "../../../components/input/FloatingInput";
import CustomSelect from "../../../components/customselect/CustomSelect";
import { yupResolver } from "@hookform/resolvers/yup";
import { ThemeContext } from "../../../context/ThemeContext";
import { EditUserSchema, EditSalesUserSchema } from "../../../utils/editUserSchema";
import { useModal } from "../../../context/ModalContext";
import api from "../../../api/axios";
import { useTranslation } from "react-i18next";
import profilepics from "../../../images/profile-pics.png";
import "../../profile/profile.css";

export default function ViewEditSalesRepModel({ user, handleUpdateToaster, loadUserList }) {
  const { t: trans } = useTranslation();

  const theme = useContext(ThemeContext);
  const { closeModal } = useModal();

  const [isEditing, setIsEditing] = useState(false);
  const [states, setStates] = useState([]);
  const [counties, setCounties] = useState([]);
  const [titleReps, setTitleReps] = useState([]);
  const mainCompanyId = theme.id;
  const [modalStateIds, setModalStateIds] = useState([]);
  const [addressCounties, setAddressCounties] = useState([]);
  const [savedCountyMap, setSavedCountyMap] = useState({});
  const [setProfilePic] = useState(profilepics); // actual saved pic
  const [modalCountiesMap, setModalCountiesMap] = useState({});
  const [modalSelectedCounties, setModalSelectedCounties] = useState({});
  const hasInitializedAddressStateRef = useRef(false);


  const schema = useMemo(() => {
    if (user.userPolicy === 'N') {
      return EditUserSchema(states, counties, titleReps, trans);
    } else {
      return EditSalesUserSchema(states, counties, titleReps, trans);
    }
  }, [user.userPolicy, states, counties, titleReps, trans]);

  const [localUser, setLocalUser] = useState({
    first_name: "",
    last_name: "",
    email: "",
    mailing_address: "",
    city: "",
    state: "",
    county: "",
    postal_code: "",
    phone: "",
    office_name: "",
    titlerep: "",
    dre_nmls: "",
    corporate_nmls: "",
  });

  const { register, handleSubmit, watch, trigger, control, setError, setValue, getValues, clearErrors, reset, formState: { errors } } = useForm({ resolver: yupResolver(schema), mode: "onChange", defaultValues: localUser });

  const addressStateValue = watch("address_state");
  const watchState = watch("state");
  const watchCounty = watch("county");
  const watchPostal = watch("postal_code");
  const [selectedCountyId, setSelectedCountyId] = useState(null)


  useEffect(() => {
    setValue("states", modalStateIds);
    setValue("counties", modalSelectedCounties);
  }, [modalStateIds, modalSelectedCounties, setValue]);

  useEffect(() => {
    if (!modalStateIds.length) return;

    // Load counties for each saved state from database
    modalStateIds.forEach((sid) => {
      if (!modalCountiesMap[sid]) {
        fetchCountiesForState(sid);
      }
    });
  }, [modalStateIds]);


  const fetchCountiesForState = async (sid) => {
    try {
      const res = await api.get('/auth/counties', {
        params: { maincompanyid: theme.id, stateId: sid }
      });

      const loadedCounties = res.data.data || [];

      setModalCountiesMap((prev) => ({
        ...prev,
        [sid]: loadedCounties
      }));

      // ⬅️ Auto-check saved counties if available
      setModalSelectedCounties((prev) => {
        const existing = prev[sid]?.length ? prev[sid] : savedCountyMap[sid] || [];
        return { ...prev, [sid]: existing };
      });
    } catch (e) {
      console.error(`Failed loading counties for ${sid}`, e);
    }
  };

  useEffect(() => {
    if (!addressStateValue) {
      setAddressCounties([]);
      try { setValue('address_county', ''); } catch (e) { /* ignore */ }
      return;
    }

    // Preserve the saved address_county on initial population
    if (!hasInitializedAddressStateRef.current) {
      hasInitializedAddressStateRef.current = true;
      api
        .get("/auth/counties", {
          params: { maincompanyid: theme.id, stateId: addressStateValue },
        })
        .then((res) => setAddressCounties(res.data.data || []))
        .catch((err) => {
          console.error("Error loading address counties (initial)", err);
          setAddressCounties([]);
        });
      return;
    }

    // Subsequent (user) changes: fetch and clear address_county so user must reselect
    api
      .get("/auth/counties", {
        params: { maincompanyid: theme.id, stateId: addressStateValue },
      })
      .then((res) => {
        setAddressCounties(res.data.data || []);
        try { setValue('address_county', ''); } catch (e) { /* ignore */ }
      })
      .catch((err) => {
        console.error("Error loading address counties", err);
        setAddressCounties([]);
      });
  }, [addressStateValue, theme.id]);

  /** ✅ Load user profile on mount */
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/get-other-user-profile/" + user.id);
        if (res.data?.status === "success" && res.data?.data) {
          const u = res.data.data;
          u.phone = u.phone ? formatMobile(u.phone) : '';
          setLocalUser(u);

          reset({
            first_name: u.first_name || "",
            last_name: u.last_name || "",
            email: u.email || "",
            username: u.username || "",
            mailing_address: u.mailing_address || "",
            city: u.city || "",
            state: u.state || "",
            county: u.county || "",
            postal_code: u.postal_code || "",
            phone: u.phone || "",
            office_name: u.office_name || "",
            titlerep: String(u.titlerep ?? ""),
            dre_nmls: u.dre_nmls || "",
            corporate_nmls: u.corporate_nmls || ""
          });

          if (u.user_type === 'S') {
            const full = res.data.salesRep;
            console.log("title", full.title);
            setValue("title", full.title || "");
            setValue("address1", full.address1 || "");
            setValue("address2", full.address2 || "");
            setValue("address_city", full.address_city || "");
            setValue("address_state", full.address_state || "");
            setValue("address_county", full.address_county || "");
            setValue("address_postal_code", full.address_zip || "");

            const sel = Array.isArray(full.selected_locations) ? full.selected_locations : [];
            if (sel.length) {
              const stateIds = [];
              const tmpCountyMap = {};

              for (const s of sel) {
                const sid = s.state_id || s.stateid || s.id;
                if (!sid) continue;

                if (!stateIds.includes(sid)) stateIds.push(sid);

                const counties = Array.isArray(s.counties) ? s.counties : [];
                tmpCountyMap[sid] = counties
                  .map((c) => c.county_id || c.countyid || c.id)
                  .filter(Boolean);
              }

              setModalStateIds(stateIds);
              setSavedCountyMap(tmpCountyMap);
            }
          }

          if (u.profile_pic) {
            setProfilePic(`${process.env.REACT_APP_ASSETS_BASE_URL}/uploads/users/${u.profile_pic}?t=${Date.now()}`);
          } else {
            setProfilePic(profilepics);
          }

        }
      } catch (err) {
        console.error("Error fetching user profile", err);
      } finally {
        //   setIsProfileLoaded(true);
      }
    })();
  }, []);

  useEffect(() => {
    if (!localUser?.titlerep) return;
    if (!titleReps.length) return;

    const selected = titleReps.find(r => String(r.id) === String(localUser.titlerep));
    if (selected) {
      setValue("titlerep", String(selected.id));
    }
  }, [titleReps, localUser]);



  /** ✅ Dropdowns */
  useEffect(() => {
    api
      .get(`/auth/states`, { params: { maincompanyid: mainCompanyId } })
      .then((res) => {
        const fetchedStates = res.data.data || [];
        const selectOption = { id: "", statename: trans("register_state") };
        setStates([selectOption, ...fetchedStates]);

      })
      .catch((err) => console.error("Failed to fetch states:", err));
  }, [mainCompanyId]);

  // ✅ Fetch counties
  useEffect(() => {
    if (!watchState) {
      setCounties([]);
      return;
    }
    api
      .get(`/auth/counties`, { params: { maincompanyid: theme.id, stateId: watchState } })
      .then((res) => {
        const fetchedCounties = res.data.data || [];
        const selectOption = { id: "", countyname: trans("register_county") };
        setCounties([selectOption, ...fetchedCounties]);
      })
      .catch((err) => console.error("Failed to fetch counties:", err));
  }, [watchState, theme.id, trans]);

  useEffect(() => {
    if (selectedCountyId && counties.length) {
      setValue("county", selectedCountyId);
    }
  }, [counties]);

  // ✅ Revalidate ZIP when necessary
  useEffect(() => {
    if (watchCounty && watchPostal) {
      trigger("postal_code");
    }
  }, [watchCounty, watchPostal, trigger]);

  // ✅ Fetch title reps
  useEffect(() => {
    if (!watchState || !watchCounty) {
      setTitleReps([]);
      return;
    }
    api
      .get(`/auth/titlereps`, {
        params: { maincompanyid: theme.id, stateId: watchState, countyId: watchCounty },
      })
      .then((res) => {
        const fetchedReps = res.data.data || [];
        const selectOption = { id: "", name: trans("register_title_rep") };
        setTitleReps([selectOption, ...fetchedReps]);
      })
      .catch((err) => {
        console.error("Error fetching title reps:", err);
        setTitleReps([]);
      });
  }, [watchState, watchCounty, theme.id, trans]);



  // ✅ Blur validation handlers
  const zipControllerRef = useRef(null);
  const emailValidRef = useRef(null);
  const usernameValidRef = useRef(null);

  const handleEmailBlur = async (eOrValue) => {
    const email = typeof eOrValue === "string" ? eOrValue : eOrValue.target?.value;
    if (!email) return;
    try {
      const res = await api.get("/auth/check-email", { params: { email, maincompanyid: theme.id, user_type: "S" } });
      if (res.data?.status !== "success") {
        emailValidRef.current = false;
        setError("email", { type: "manual", message: trans("register_email_exists") });
      } else {
        emailValidRef.current = true;
        clearErrors("email");
      }
    } catch {
      setError("email", { type: "manual", message: trans("register_email_validation_failed") });
    }
  };

  const handleUsernameBlur = async (eOrValue) => {
    const username = typeof eOrValue === "string" ? eOrValue : eOrValue.target?.value;
    if (!username) return;
    try {
      const res = await api.get("/auth/check-username", { params: { username, maincompanyid: theme.id } });
      if (res.data?.status !== "success") {
        usernameValidRef.current = false;
        setError("username", { type: "manual", message: trans("register_username_exists") });
      } else {
        usernameValidRef.current = true;
        clearErrors("username");
      }
    } catch {
      setError("username", { type: "manual", message: trans("register_username_validation_failed") });
    }
  };

  const handlePostalBlur = async (eOrValue) => {
    const zip = typeof eOrValue === "string" ? eOrValue : eOrValue.target?.value;
    if (!zip) return;

    if (zipControllerRef.current) {
      try {
        zipControllerRef.current.abort();
      } catch { }
    }

    const controller = new AbortController();
    zipControllerRef.current = controller;

    const stateid = getValues("state");
    const countyid = getValues("county") || selectedCountyId;

    try {
      const res = await api.post(
        "/auth/verify-zip",
        { zipcode: zip, maincompanyid: theme.id, stateid, countyid },
        { signal: controller.signal }
      );
      if (res.data?.status === "fail") {
        setError("postal_code", { type: "manual", message: trans("register_zip_invalid") });
      } else {
        const state = res.data?.data?.stateid;
        const county = res.data?.data?.countyid;
        setValue('state', '');
        setTimeout(() => {
          setValue('state', state);
          setSelectedCountyId(county);
          clearErrors("postal_code");
        }, 100)
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        setError("postal_code", { type: "manual", message: trans("register_zip_validation_failed") });
      }
    }
  };

  // ✅ Field bindings
  const emailReg = register("email");
  const usernameReg = register("username");
  const postalReg = register("postal_code");

  const onSubmit = async (data) => {
    if (!isEditing) {
      console.log("⏸ Not editing — ignore submit");
      return;
    }

    console.log("✅ onSubmit triggered manually:", data);
    try {
      const res = await api.put("/update-other-user-profile/" + user.id, {
        ...data,
        phone: localUser.phone.replaceAll(" - ", ""),
        profile_pic: localUser.profile_pic,
      });

      if (res.data?.status === "success") {
        const updatedUser = { ...localUser, ...data };
        setLocalUser(updatedUser);
        // updateUser(updatedUser);
        handleUpdateToaster({ message: 'Profile updated successfully!', type: 'success', visible: true });
        setTimeout(() => {
          handleUpdateToaster((prev) => ({ ...prev, visible: false }));
        }, 2000)
        setIsEditing(false);
        loadUserList();
        closeModal();
      } else {
        alert("Failed to update profile.");
      }
    } catch (err) {
      console.error("❌ Update profile error:", err);
      alert("Error updating profile.");
    }
  };

  const formatMobile = (input) => {
    const digits = input.replace(/[^0-9]/g, "").slice(0, 20);
    let formatted = "";
    for (let i = 0; i < digits.length; i++) {
      if (i === 3 || i === 6) formatted += " - ";
      formatted += digits[i];
    }
    return formatted;
  };

  const handlePhoneChange = (value) => {
    const formatted = formatMobile(value);
    setValue('phone', formatted);
  };

  /** ✅ Render */
  return (
    <>
      <div className="user-p1 popupinner-wdth">
        <div className="user-form1">
          {/*  <div className="user-upload-img">
            <div className="user-info">
              <div className="user-p-pics">
                <div className="uploader ">
                  <input id="imgInput" type="file" accept="image/*" hidden onChange={handleImageChange} />
                  <label htmlFor="imgInput" className="upload-box" aria-label="Upload image">
                    {!isUploading && (
                      <div className="upload-hint">
                        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                          <path d="M12 16v-8m0 0l-3 3m3-3l3 3M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <span>Upload</span>
                      </div>
                    )}
                  </label>
                  <button className="edit-up-img" onClick={handleImageSelect} >
                    Click here to change the image
                  </button>
                </div>
              </div>
              <div class="user-p-name"><h3>Welcome, Mayank 123</h3></div>
              <div class="user-p-desc"><p>Click on image to change. This image will appear on your net sheets.</p></div>
            </div>
          </div>*/}
          <div className="popupinner-wdth-a">
            <div className="popupinner-wdth-b">
              <div className="reg-f-container">
                <form onSubmit={handleSubmit(onSubmit)}>

                  {/* First & Last Name */}
                  <div className="frm-field-2">
                    <div className="form-group">
                      <FloatingInput label={trans("register_first_name")} disabled={!isEditing} {...register("first_name")} />
                      {errors.first_name && <p className="field-error">{errors.first_name.message}</p>}
                    </div>
                    <div className="form-group">
                      <FloatingInput label={trans("register_last_name")} disabled={!isEditing} {...register("last_name")} />
                      {errors.last_name && <p className="field-error">{errors.last_name.message}</p>}
                    </div>
                  </div>

                  {/* Email Fields */}
                  <div className="frm-field-2">
                    <div className="form-group">
                      <FloatingInput label={trans("register_email")} disabled {...emailReg} onBlur={handleEmailBlur} />
                      {errors.email && <p className="field-error">{errors.email.message}</p>}
                    </div>
                    {/* Username */}
                    <div className="form-group">
                      <FloatingInput label={trans("register_username")} disabled {...usernameReg} onBlur={handleUsernameBlur} />
                      {errors.username && <p className="field-error">{errors.username.message}</p>}
                    </div>
                  </div>

                  {/* Address */}
                  <div className="frm-field-2">
                    <div className="form-group">
                      <FloatingInput label={trans("register_address")} disabled={!isEditing} {...register("mailing_address")} />
                      {errors.mailing_address && <p className="field-error">{errors.mailing_address.message}</p>}
                    </div>
                    <div className="form-group">
                      <FloatingInput label={trans("register_city")} disabled={!isEditing} {...register("city")} />
                      {errors.city && <p className="field-error">{errors.city.message}</p>}
                    </div>
                  </div>

                  {/* State & County */}
                  <div className="frm-field-2">
                    <div className="form-group">
                      <Controller
                        name="state"
                        control={control}
                        render={({ field }) => (
                          <CustomSelect options={states}
                          label={"State"}
                          placeholder={trans("register_state")} disabled={!isEditing} onChange={field.onChange} value={field.value} />
                        )}
                      />
                      {errors.state && <p className="field-error">{errors.state.message}</p>}
                    </div>
                    <div className="form-group">
                      <Controller
                        name="county"
                        control={control}
                        render={({ field }) => (
                          <CustomSelect options={counties} 
                          label={"County"}
                          placeholder={trans("register_county")} disabled={!isEditing} onChange={field.onChange} value={field.value} />
                        )}
                      />
                      {errors.county && <p className="field-error">{errors.county.message}</p>}
                    </div>
                  </div>

                  {/* ZIP & Phone */}
                  <div className="frm-field-2">
                    <div className="form-group">
                      <FloatingInput label={trans("register_zip")} disabled={!isEditing} {...postalReg} onBlur={handlePostalBlur} maxLength={5} />
                      {errors.postal_code && <p className="field-error">{errors.postal_code.message}</p>}
                    </div>
                    <div className="form-group">
                      <FloatingInput label={trans("register_phone")} disabled={!isEditing} {...register("phone")} onChange={(e) => handlePhoneChange(e?.target?.value)} />
                      {errors.phone && <p className="field-error">{errors.phone.message}</p>}
                    </div>
                  </div>

                  {/* Office & Title Rep */}

                  {user.userPolicy === 'N' ? (
                    <>
                      <div className="frm-field-2">
                        <div className="form-group">
                          <FloatingInput label={trans("register_office")} disabled={!isEditing} {...register("office_name")} />
                          {errors.office_name && <p className="field-error">{errors.office_name.message}</p>}
                        </div>
                        <div className="form-group">
                          <Controller
                            name="titlerep"
                            control={control}
                            render={({ field }) => (
                              <CustomSelect options={titleReps} 
                              label={"Title Rep"}
                              disabled={!isEditing} placeholder={trans("register_title_rep")} onChange={field.onChange} value={field.value} />
                            )}
                          />
                          {errors.titlerep && <p className="field-error">{errors.titlerep.message}</p>}
                        </div>
                      </div>

                      {/* BRE */}
                      <div className="frm-field-2">
                        <div className="form-group">
                          <FloatingInput label={trans("register_bre")} disabled={!isEditing} {...register("dre_nmls")} />
                        </div>
                        <div className="form-group">
                          <FloatingInput label={trans("register_corp_bre")} disabled={!isEditing} {...register("corporate_nmls")} />
                        </div>
                      </div>

                      {/* ✅ Form Buttons */}
                      <div className="full-width-container-btn">
                        <div className="left-btn-container">                          
                            <button type="button" onClick={() => closeModal()} className="submit-btn btn-bg inpt-btn w-120 discard-btn">
                              Discard
                            </button>                          
                        </div>
                        <div className="right-btn-container">
                          {/* {isEditing && (
                            <button type="submit" disabled={isSubmitting} className="submit-btn btn-bg inpt-btn w-190" style={{ backgroundColor: theme.primary_color }}>
                              {isSubmitting ? "Saving..." : "Save Changes"}
                            </button>
                          )} */}
                        </div>
                      </div>

                {/*     {!isEditing && (
                        <div className="full-width-container-btn">
                          <div className="right-btn-container">
                            <button type="button" onClick={() => setIsEditing(true)} className="submit-btn btn-bg inpt-btn w-190" style={{ backgroundColor: theme.primary_color }}>
                              Update my profile
                            </button>
                          </div>
                        </div>
                      )} */}
                    </>
                  ) : (
                    <>
                      {/* STATE & COUNTY SELECTION */}
                      < div className="state-county-wrapper">

                        {/* Section Title */}
                        <div className="section-label">Select a state to choose counties</div>

                        {/* Two Column Grid */}
                        <div className="state-county-grid">

                          {/* LEFT COLUMN — STATES */}
                          <div className="state-list">
                            {states.map((s) => {
                              const sid = s.id || s.stateid || s.value;
                              const checked = modalStateIds.includes(sid);

                              return (
                                <label key={sid} className="state-item">
                                  <input
                                    type="checkbox"
                                    checked={checked} disabled={!isEditing}
                                  />
                                  <span>{s.statename || s.name}</span>
                                </label>
                              );
                            })}
                          </div>

                          {/* RIGHT COLUMN — COUNTY PANELS */}
                          <div className="county-panel-container">
                            {modalStateIds.map((sid) => {
                              const sObj =
                                states.find((s) => (s.id || s.stateid || s.value) === sid) || {
                                  id: sid,
                                };

                              const counties = modalCountiesMap[sid] || [];
                              const selectedForState = modalSelectedCounties[sid] || [];

                              return (
                                <div key={sid} className="county-panel">

                                  {/* Panel Header */}
                                  <div className="county-panel-header">
                                    <span className="county-panel-title">
                                      {sObj.statename || sObj.name || `State ${sid}`}
                                    </span>
                                    <span className="county-selected-count">
                                      {selectedForState.length} selected
                                    </span>
                                  </div>

                                  {/* COUNTY CHECKBOX LIST */}
                                  <div className="county-list">
                                    {counties.map((c) => {
                                      const cid = c.id || c.countyid || c.value;
                                      const checked = selectedForState.includes(cid);

                                      return (
                                        <label key={cid} className="county-item">
                                          <input
                                            type="checkbox"
                                            checked={checked}
                                            disabled={!isEditing}

                                          />
                                          <span>{c.countyname || c.name}</span>
                                        </label>
                                      );
                                    })}
                                  </div>

                                  {/* SELECTED TAGS */}
                                  <div className="county-tags">
                                    {selectedForState.map((cid) => {
                                      const cObj =
                                        (modalCountiesMap[sid] || []).find(
                                          (c) => (c.id || c.countyid || c.value) === cid
                                        ) || { id: cid };

                                      return (
                                        <div key={cid} className="county-tag">
                                          <span>{cObj.countyname || cObj.name || `County ${cid}`}</span>
                                          <button
                                            type="button"
                                            disabled={!isEditing}
                                          >
                                            ×
                                          </button>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>

                      <div className="frm-field-2 lbl-select-state">Sales Rep Address</div>

                      {/* Address & city   */}
                      <div className="frm-field-2">
                        <div className="form-group">
                          <FloatingInput label="Mailing Address 1" disabled={!isEditing} {...register("address1")} />
                          {errors.mailing_address && <p className="field-error">{errors.mailing_address.message}</p>}
                        </div>
                        <div className="form-group">
                          <FloatingInput label="Mailing Address 2" disabled={!isEditing} {...register("address2")} />
                        </div>
                        <div className="form-group">
                          {errors.city && <p className="field-error">{errors.city.message}</p>}
                          <FloatingInput label="City" disabled={!isEditing} {...register("address_city")} />
                        </div>
                      </div>

                      {/* State & county */}
                      <div className="frm-field-2">
                        <div className="form-group">
                          <Controller
                            name="address_state"
                            control={control}
                            render={({ field }) => {
                              const options = (states || []).map((s) => ({
                                value: s.id,
                                label: s.statename,
                              }));

                              // Find the selected item based on the numeric ID
                              const selectedOption =
                                options.find((opt) => Number(opt.value) === Number(field.value)) || null;

                              return (
                                <CustomSelect
                                  options={options}
                                  label={"State"}
                                  placeholder={
                                    selectedOption?.label || "Select State" // ✅ text, not object
                                  }
                                  value={selectedOption} // ✅ actual selected object
                                  onChange={(val /*, option */) => field.onChange(val)}
                                  disabled={!isEditing}
                                />
                              );
                            }}
                          />
                        </div>
                        <div className="form-group">
                          <Controller
                            name="address_county"
                            control={control}
                            render={({ field }) => {
                              const options = (addressCounties || []).map((c) => ({
                                value: c.id,
                                label: c.countyname,
                              }));

                              const selectedOption =
                                options.find((opt) => Number(opt.value) === Number(field.value)) || null;

                              return (
                                <CustomSelect
                                  options={options}
                                  label={"County"}
                                  placeholder={selectedOption?.label || "Select County"}
                                  value={selectedOption}
                                  onChange={(val /*, option */) => field.onChange(val)}
                                  disabled={!isEditing}
                                />
                              );
                            }}
                          />
                        </div>
                        <div className="form-group">
                          <FloatingInput label="ZIP Code" disabled={!isEditing} {...register("address_postal_code")} />
                        </div>
                      </div>

                      <div className="full-width-container-btn">
                        <div className="left-btn-container">
                          <button type="button" onClick={() => closeModal()} className="submit-btn btn-bg inpt-btn w-120 discard-btn">
                            Discard
                          </button>
                        </div>
                        <div className="right-btn-container">

                        </div>
                      </div>
                    </>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div >
      </div >
    </>
  );
}
